import NoneChip from '@/components/chip/none-chip';
import StatusChip, { ChipStatus } from '@/components/chip/status-chip';
import ConnectorLink from '@/components/connector-link';
import ReadonlyView from '@/components/form/readonly-view';
import { ReadonlyField } from '@/components/form/readonly-view/helpers';
import InlineToast from '@/components/toasts/inline';
import { VERTEX_AI_REGION_OPTS } from '@/lib/helpers';
import { ConnectorModel } from '@/lib/models/connector/connector.model';
import { AzureOpenAIPrivateModelConfig } from '@/lib/models/private-model/azure-openai-private-model.model';
import { PrivateModelModel } from '@/lib/models/private-model/private-model.model';
import { VertexPrivateModelConfig } from '@/lib/models/private-model/vertex-private-model.model';
import { FC, ReactNode, useMemo } from 'react';

interface Props {
  model: PrivateModelModel | null;
  connector?: ConnectorModel;
}

const PrivateModelReadonlyView: FC<Props> = ({ model, connector }) => {
  const [data, fields] = useMemo(() => {
    if (!model) {
      return [{}, []];
    }

    const { name, modelId, niceStatusMessage, status, provider, config } = model;

    const hasConnector = !!connector;

    const fields: ReadonlyField[] = [
      { label: 'Name', prop: 'name' },
      { label: 'Model ID', prop: 'modelId' },
      { label: 'Connector', prop: 'connectorLabel' },
      { label: 'Status', prop: 'status' },
      { label: 'Status Message', prop: 'statusMessage' },
    ];

    const data: Record<string, ReactNode> = {
      name,
      modelId,
      connectorLabel: hasConnector ? (
        <ConnectorLink connector={connector} variant="outlined" showType />
      ) : (
        <NoneChip />
      ),
      status: status ? (
        <StatusChip value={status as ChipStatus} size="small" />
      ) : (
        <NoneChip notAvailable />
      ),
      statusMessage: niceStatusMessage ? (
        <InlineToast
          hideIcon
          message={niceStatusMessage}
          level={status === 'active' ? 'info' : 'error'}
        />
      ) : (
        <NoneChip />
      ),
    };

    if (provider === 'azure') {
      fields.push({ label: 'Configuration', prop: 'config-section', propType: 'section' });
      fields.push({ label: 'Deployment ID', prop: 'deploymentId' });

      const { deploymentId } = config as AzureOpenAIPrivateModelConfig;
      data.deploymentId = deploymentId;
    }

    if (provider === 'vertex_ai') {
      fields.push({ label: 'Configuration', prop: 'config-section', propType: 'section' });
      fields.push({ label: 'Region', prop: 'region' });

      const { region } = config as VertexPrivateModelConfig;
      data.region = region ? VERTEX_AI_REGION_OPTS.find(({value}) => value === region)?.label : <NoneChip />;
    }

    return [data, fields];
  }, [model, connector]);

  if (!model) {
    return null;
  }

  return <ReadonlyView fields={fields} data={data} />;
};

export default PrivateModelReadonlyView;
